export interface Delivery {
  status: 'sending' | 'sent' | 'uncertain'
  attempt: string
  email: string
  updatedAt: number
}
export function readDelivery(value: unknown): Delivery | null {
  if (!value) return null
  try {
    const d = typeof value === 'string' ? JSON.parse(value) : value
    if (
      ['sending', 'sent', 'uncertain'].includes(d.status) &&
      typeof d.attempt === 'string' &&
      typeof d.email === 'string' &&
      Number.isFinite(d.updatedAt)
    )
      return d
  } catch {}
  throw new Error(
    'Email status could not be read. Contact support before sending again.',
  )
}
export interface DeliveryStore {
  change(
    update: (current: string | null) => string | undefined,
  ): Promise<{ committed: boolean; value: string | null }>
}
export async function deliverOnce(
  store: DeliveryStore,
  email: string,
  resend: boolean,
  send: () => Promise<void>,
  now = () => Date.now(),
): Promise<void> {
  const attempt =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : String(now()) + '-' + Math.random().toString(36).slice(2)
  const claim: Delivery = {
    status: 'sending',
    attempt,
    email,
    updatedAt: now(),
  }
  const result = await store.change((raw) => {
    const prev = readDelivery(raw)
    if (
      prev &&
      (!resend ||
        (prev.status === 'sending' && now() - prev.updatedAt < 300000))
    )
      return undefined
    return JSON.stringify(claim)
  })
  if (!result.committed)
    throw new Error(
      readDelivery(result.value)?.status === 'sent'
        ? 'Already emailed. Use Resend manually if another copy is needed.'
        : 'A send is in progress or needs review. Check the inbox before explicitly retrying.',
    )
  let accepted = false
  try {
    await send()
    accepted = true
    const saved = await store.change((raw) =>
      readDelivery(raw)?.attempt === attempt
        ? JSON.stringify({ ...claim, status: 'sent', updatedAt: now() })
        : undefined,
    )
    if (!saved.committed) throw new Error('Receipt changed during send.')
  } catch (e) {
    try {
      await store.change((raw) =>
        readDelivery(raw)?.attempt === attempt
          ? JSON.stringify({
              ...claim,
              status: accepted ? 'sent' : 'uncertain',
              updatedAt: now(),
            })
          : undefined,
      )
    } catch {}
    // Keep the underlying cause (HTTP status, timeout, network/CORS) visible:
    // a generic message made 413/500/timeouts indistinguishable (2026-10-24).
    const cause = deliveryFailureDetail(e)
    throw new Error(
      accepted
        ? 'Email accepted, but the receipt could not be saved. Check the inbox; do not resend automatically.'
        : 'Delivery could not be confirmed' +
          (cause ? ': ' + cause + '.' : '.') +
          ' Check the inbox, then use Resend manually if needed.',
    )
  }
}
export function deliveryFailureDetail(e: unknown): string {
  const message =
    e && typeof (e as { message?: unknown }).message === 'string'
      ? (e as { message: string }).message
      : typeof e === 'string'
      ? e
      : ''
  return message.trim().replace(/[.]+$/, '').slice(0, 300)
}
